/*
  Kalmora — cinematic.js
  Capa cinematográfica con GSAP + ScrollTrigger (theme/assets/gsap.min.js y ScrollTrigger.min.js, v3.13).
  Convive con motion.js (apariciones, escena fija, línea de progreso): aquí solo van los efectos con scrub.

  Atributos que lee:
  - [data-cine-expand]    Sección que se fija mientras su [data-cine-frame] pasa de tarjeta recortada a pantalla
                          completa (se anima clip-path: la imagen nunca se escala). [data-cine-copy] entra al final.
  - [data-cine-grow]      El marco de la imagen crece desde un recorte interior hasta su tamaño al entrar en pantalla.
  - [data-cine-parallax]  La imagen interior se desplaza más despacio que el scroll (solo fotos de escena o detalle).

  Fidelidad: ningún efecto escala ni deforma el producto; solo se animan recortes y desplazamientos.
  Con prefers-reduced-motion: reduce no se crea ningún efecto y todo se ve en su estado final.
*/
(() => {
  if (!window.gsap || !window.ScrollTrigger) return;
  const { gsap, ScrollTrigger } = window;
  gsap.registerPlugin(ScrollTrigger);

  const mm = gsap.matchMedia();

  function build(scope) {
    mm.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        mobile: '(max-width: 767px)',
      },
      (context) => {
        const { motion, mobile } = context.conditions;
        if (!motion) return;

        /* 1. Expansión: la sección se fija y la imagen se abre hasta ocupar toda la pantalla */
        scope.querySelectorAll('[data-cine-expand]').forEach((section) => {
          const stage = section.querySelector('.expand__stage') || section;
          const frame = section.querySelector('[data-cine-frame]');
          const copy = section.querySelectorAll('[data-cine-copy]');
          if (!frame) return;

          const from = mobile ? 'inset(22% 8% 22% 8% round 20px)' : 'inset(16% 26% 16% 26% round 28px)';
          const timeline = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger: stage,
              start: 'top top',
              end: '+=110%',
              scrub: 0.6,
              pin: stage,
              anticipatePin: 1,
            },
          });

          timeline
            .fromTo(frame, { clipPath: from }, { clipPath: 'inset(0% 0% 0% 0% round 0px)', duration: 1 })
            .fromTo(
              copy,
              { autoAlpha: 0, y: 28 },
              { autoAlpha: 1, y: 0, duration: 0.35, stagger: 0.08, ease: 'power2.out' },
              0.62
            );
        });

        /* 2. Marco que crece al entrar (sin escalar la imagen: se abre su recorte) */
        scope.querySelectorAll('[data-cine-grow]').forEach((el) => {
          gsap.fromTo(
            el,
            { clipPath: mobile ? 'inset(6% 6% 6% 6% round 24px)' : 'inset(10% 12% 10% 12% round 32px)' },
            {
              clipPath: 'inset(0% 0% 0% 0% round 16px)',
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top 92%', end: 'top 35%', scrub: 0.6 },
            }
          );
        });

        /* 3. Parallax de la foto interior (la imagen es más alta que su marco y se desplaza dentro de él) */
        scope.querySelectorAll('[data-cine-parallax]').forEach((el) => {
          const img = el.querySelector('img');
          if (!img) return;
          el.classList.add('is-parallax');
          gsap.fromTo(
            img,
            { yPercent: -7 },
            {
              yPercent: 7,
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
            }
          );
        });

        return () => scope.querySelectorAll('.is-parallax').forEach((el) => el.classList.remove('is-parallax'));
      }
    );
  }

  build(document);

  // Las imágenes diferidas cambian la altura de la página: se recalculan los puntos de ScrollTrigger
  let refreshTimer = 0;
  const refreshSoon = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
  };
  window.addEventListener('load', refreshSoon);
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (!img.complete) img.addEventListener('load', refreshSoon, { once: true });
  });

  document.addEventListener('shopify:section:load', () => {
    mm.revert();
    build(document);
    ScrollTrigger.refresh();
  });
})();
